import {
    AugmentedCartItemPayload,
    CartItemPayload,
    CartPayload,
    CustomerCarts,
    ShopwareCart,
    VoucherWithDynamicPriceCartPayloadItem,
    VoucherCartPayloadItem,
} from '../Cart/Types';
import { translateIdToName } from '../Context/AccountContext';
import { Account } from '../MyLom/Account/EditAccount/Types';
import { isVoucherWithDynamicPrice, isVoucherPayload } from '../Utils';
import { CustomerId } from './Account/Types';

declare global {
    interface Window {
        __GGShopUrl: string | undefined;
    }
}

const shopUrl = window.__GGShopUrl || '/shop';

export function readCart(
    account: Account,
    onSuccess: (json: CustomerCarts, cartMessages: string[]) => void,
    onError: (json: any) => void,
    onFailure?: (error: any) => void,
) {
    fetch(shopUrl + '/lbasket', {
        method: 'get',
        credentials: 'include',
    })
        .then((response) => response.json())
        .then((json) => {
            if (json.success === true) {
                if (json.data && json.data.items.length > 0) {
                    augmentCart(account, json.data, onSuccess, onError, onFailure);
                } else {
                    onSuccess(convertExternalToInternalFormat(account, []), json.data.messages);
                }
            } else {
                onError(json);
            }
        })
        .catch((error) => {
            console.error('Server failure while fetching cart: ', error);
            if (onFailure && typeof onFailure === 'function') {
                onFailure(error);
            }
        });
}

/**
 * Load additional cart (product) data from Neos endpoint
 */
function augmentCart(
    account: Account,
    rawCart: CartPayload & { messages: string[] },
    onSuccess: (json: CustomerCarts, cartMessages: string[]) => void,
    onError: (json: any) => void,
    onFailure?: (error: any) => void,
) {
    fetch('/api/account/' + account.id + '/cart/augment', {
        method: 'post',
        body: JSON.stringify(rawCart),
    })
        .then((response) => response.json())
        .then((json) => {
            if (json.errors === true) {
                onError(json.errors);
            } else {
                onSuccess(convertExternalToInternalFormat(account, json.data), rawCart.messages);
            }
        })
        .catch((error) => {
            if (onFailure && typeof onFailure === 'function') {
                onFailure(error);
            }
            console.error('Server failure while fetching cart: ', error);
        });
}

export function saveCart(
    customerId: CustomerId,
    cart: ShopwareCart,
    defaultConsultantId: string | null,
    onSuccess: (success: boolean) => void,
    onError: (json: any) => void,
    onFailure?: (error: any) => void,
) {
    fetch(shopUrl + '/lbasket/put', {
        body: JSON.stringify(convertInternalToExternalFormat(cart, customerId, defaultConsultantId)),
        method: 'post',
        // this causes a preflight ``OPTIONS`` request in Chrome (in CORS mode)
        // should be decided if the "prettier" request header is worth the extra request
        // headers: {
        //     'Content-Type': 'application/json'
        // },
        credentials: 'include',
    })
        .then((response) => response.json())
        .then((json) => {
            if (json.success === true) {
                onSuccess(json.data);
            } else {
                onError(json);
            }
        })
        .catch((error) => {
            console.error('Server failure while pushing cart: ', error);
            if (onFailure && typeof onFailure === 'function') {
                onFailure(error);
            }
        });
}

function convertInternalToExternalFormat(
    cart: ShopwareCart,
    customerId: CustomerId,
    defaultConsultantId: string | null,
): CartPayload {
    const payload: CartPayload = {
        data: {
            items: [],
            consultantId: defaultConsultantId,
        },
    };

    const carts = cart.carts;

    if (carts === null) {
        return payload;
    }

    Object.entries(carts).map(([cartCustomerId, cartContent]) => {
        const isProfile = cartCustomerId !== customerId;
        cartContent.items.map((item) => {
            const payloadItem: CartItemPayload | VoucherWithDynamicPriceCartPayloadItem | VoucherCartPayloadItem = {
                articleNumber: item.articleNumber,
                quantity: item.quantity,
                profileLabel: isProfile ? translateIdToName(cartCustomerId).name : null,
                type: item.type,
            };

            if (isVoucherWithDynamicPrice(payloadItem)) {
                payloadItem.voucher_custom_value = (item.priceInCents / 100).toString();
            }

            if (isVoucherPayload(payloadItem)) {
                payloadItem.voucher_design_name = item.voucher_design_name || '';
            }

            payload.data.items.push(payloadItem);
        });
    });

    return payload;
}
function convertExternalToInternalFormat(
    account: Account,
    augmentedCartItems: AugmentedCartItemPayload[],
): CustomerCarts {
    // Generate empty cart inlcuding profile segments
    const carts: CustomerCarts = {};

    carts[account.id] = {
        items: [],
    };
    account.profiles.map((profile) => {
        carts[profile.id] = {
            items: [],
        };
    });

    Object.values(augmentedCartItems).map((augmentedCartItem) => {
        if (augmentedCartItem.profileLabel === null) {
            carts[account.id].items.push(augmentedCartItem);
        } else {
            const foundProfile = account.profiles.find(
                (profile) => augmentedCartItem.profileLabel === profile.profileName,
            );
            if (foundProfile) {
                carts[foundProfile.id].items.push(augmentedCartItem);
            }
        }
    });

    return carts;
}
